import { useLazyQuery } from "@apollo/client";
import {
  AiStrategy,
  QuickWinHandler,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { QUERY_LLM_STRATEGIES, QUERY_QUICK_WIN_HANDLERS } from "../../common";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ManageLlmStrategiesParamList } from "../../../../types";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import {
  Badge,
  Card,
  FlatActionList,
  FlatActionListItem,
  Page,
  Stack,
  Text,
  View,
} from "@towersystems/roam-common-native";
import { useTranslation } from "react-i18next";
import { useFocusEffect } from "@react-navigation/native";
import {
  useGlobalSettingsProvider,
  useGlobalSettingsReponseProvider,
} from "../../../../utilities/global-settings/hooks";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";
import { useLlmQuickWinsProvider } from "../../../../utilities/llm-quick-wins";

export type ManageLlmStrategiesLandingScreenProps = NativeStackScreenProps<
  ManageLlmStrategiesParamList,
  "ManageLlmStrategiesLandingScreen"
>;

export const ManageLlmStrategiesLandingScreen = ({
  navigation,
}: ManageLlmStrategiesLandingScreenProps) => {
  const { currentVendor } = useCurrentVendorProvider();
  const { strategies: llmQuickWinStrategies, refreshStrategies } =
    useLlmQuickWinsProvider();

  const [aiStrategies, setAiStrategies] = React.useState<
    AiStrategy[] | undefined
  >(undefined);

  const { globalSettings, queryGlobalSettings } =
    useGlobalSettingsReponseProvider();

  const { t } = useTranslation("settings");
  const { llmStrategyId, aiImageStrategyId } = globalSettings || {};

  const [queryAiStrategies] = useLazyQuery<{
    aiStrategies: {
      items: AiStrategy[];
    };
  }>(QUERY_LLM_STRATEGIES);

  const transitionEnded = useHasTransitionEnded(navigation);

  useFocusEffect(
    React.useCallback(() => {
      (async () => {
        try {
          const [aiStrategiesResult, _] = await Promise.all([
            queryAiStrategies(),
            refreshStrategies(),
            queryGlobalSettings(), // refresh global settings to get latest llmStrategyId
          ]);
          setAiStrategies(aiStrategiesResult.data?.aiStrategies.items);
        } catch (error) {}
      })();
    }, [])
  );

  useFocusEffect(
    React.useCallback(() => {
      if (!currentVendor.aiTermsAccepted) {
        const id = setTimeout(() => {
          navigation.navigate("AcceptAiTermsScreen");
        }, 300);
        return () => clearTimeout(id);
      }
    }, [transitionEnded, currentVendor.aiTermsAccepted])
  );

  if (
    aiStrategies === undefined ||
    llmQuickWinStrategies === undefined ||
    !transitionEnded ||
    !currentVendor.aiTermsAccepted
  ) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  const aiListItems: FlatActionListItem[] = aiStrategies.map((aiStrategy) => ({
    key: aiStrategy.id,
    title: aiStrategy.name || "Unnamed strategy",
    subTitle: aiStrategy.config.code || "",
    titleContent({ title, subTitle }) {
      return (
        <View>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {title}
            <View style={{ marginLeft: 8 }}>
              <Badge>{aiStrategy.aiType}</Badge>
            </View>
          </View>
          {subTitle}
        </View>
      );
    },
    onPress: () => {
      navigation.navigate("ViewLlmStrategyRootScreen", {
        screen: "ViewLlmStrategyScreen",
        llmStrategyId: aiStrategy.id,
      });
    },
  }));

  const numLlmStrategies = aiStrategies.filter(
    (s) => s.config.code !== "openai_dalle"
  ).length;
  const numImageGeneratingStrategies = aiStrategies.filter(
    (s) => s.config.code === "openai_dalle"
  ).length;

  return (
    <Page
      title={t("title.manage_llm_strategies")}
      secondaryTitle={t("title.manage_llm_strategies_secondary")}
    >
      <Stack vertical>
        <Card title="Selected AI Models">
          <FlatActionList
            items={[
              {
                key: "quick_win_ai_strategy",
                title: llmStrategyId
                  ? "Text Based AI Model"
                  : "Text Based AI Model",
                subTitle:
                  "Select which AI model to use for text based features",
                isAction: !llmStrategyId,
                disabled: numLlmStrategies === 0,

                componentRight: llmStrategyId
                  ? () => (
                      <Text fontWeight="bold" color="action" variant="body">
                        {aiStrategies.find((s) => s.id === llmStrategyId)
                          ?.name || "Unnamed strategy"}
                      </Text>
                    )
                  : () => <View />, // requestd by LUKE
                onPress: () => {
                  navigation.navigate("SelectLlmQuickWinStrategyScreen");
                },
              },
              {
                key: "quick_win_ai_generating_strategy",
                title: aiImageStrategyId
                  ? "Image Generating AI Model"
                  : "Image Generating AI Model",
                subTitle:
                  "Select which AI model to use for image generation features",
                isAction: !aiImageStrategyId,
                disabled: numImageGeneratingStrategies === 0,
                componentRight: aiImageStrategyId
                  ? () => (
                      <Text fontWeight="bold" color="action" variant="body">
                        {aiStrategies.find((s) => s.id === aiImageStrategyId)
                          ?.name || "Unnamed strategy"}
                      </Text>
                    )
                  : () => <View />, // requestd by LUKE
                onPress: () => {
                  navigation.navigate("SelectImageQuickWinStrategyScreen");
                },
              },
            ]}
          />
        </Card>
        <Card title="AI Models">
          <FlatActionList
            items={[
              ...aiListItems,
              {
                key: "add_new_ai_strategy",
                title: "Setup new AI Model",
                isAction: true,
                onPress: () => {
                  navigation.navigate("CreateLlmStrategyRootScreen", {
                    screen: "CreateLlmStrategyScreen",
                  });
                },
              },
            ]}
          />
        </Card>

        <Card title="AI Features">
          <FlatActionList
            items={llmQuickWinStrategies.map((handler) => ({
              key: handler.code,
              title: handler.handler.name,
              subTitle: handler.handler.description,
              titleContent({ title, subTitle }) {
                const disabledMarkup = handler.enabled == false && (
                  <Badge style={{ marginLeft: 8 }}>Disabled</Badge>
                );
                return (
                  <View>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      {title}
                      {disabledMarkup}
                    </View>
                    <View>{subTitle}</View>
                  </View>
                );
              },
              onPress: () => {
                navigation.navigate("ManageQuickWinHandlerScreen", {
                  quickWinHandlerCode: handler.code,
                });
              },
            }))}
          />
        </Card>
      </Stack>
    </Page>
  );
};
